import { faDot } from '@awesome.me/kit-4a6bf60416/icons/classic/solid';

import { ImageWithLoading } from '@/components/blocks/section-image/image-with-loading';
import { ContentDate } from '@/components/page-sections/content-date';
import { Badge } from '@/components/ui/badge';
import { ContentContainer } from '@/components/ui/content-container';
import { Icon } from '@/components/ui/icon';
import { Heading2, Heading3, Paragraph } from '@/components/ui/typography';
import { formatProfileNames, normalizeProfiles } from '../../profile-list';
import type { BlogWidgetProps } from '../definition';

export function GridVariant({ title, blogs }: BlogWidgetProps) {
  if (blogs.length === 0) return null;

  return (
    <ContentContainer>
      {title && (
        <div className="mb-8 text-left">
          <Heading2 className="mb-2">{title}</Heading2>
          <div className="flex items-center gap-2 mx-auto">
            <div className="h-[2px] w-4 bg-primary rounded-full" />
            <div className="h-[2px] w-16 bg-primary rounded-full" />
          </div>
        </div>
      )}

      <div className="grid gap-4 md:gap-8 md:grid-cols-2">
        {blogs.map((blog, index) => {
          const authors = normalizeProfiles(blog.authors);
          const authorLabel = formatProfileNames(authors);

          return (
            <article
              key={blog.id || index}
              className="group/blog-item flex flex-col gap-6 rounded-lg border border-border bg-card text-card-foreground p-6 transition-all hover:shadow-md"
            >
              {blog.imageUrl && (
                <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-muted">
                  <ImageWithLoading
                    image={{ url: blog.imageUrl }}
                    alt={blog.title || 'Blog image'}
                    className="h-full w-full object-cover"
                    inlineSvg={false}
                  />
                </div>
              )}
              <div className="flex flex-col gap-2">
                {blog.title && (
                  <Heading3 className="text-2xl group-hover/blog-item:text-primary transition-colors">
                    {blog.title}
                  </Heading3>
                )}
                {blog.summary && (
                  <Paragraph className="text-base text-muted-foreground line-clamp-4 leading-tight">
                    {blog.summary}
                  </Paragraph>
                )}
                {blog.topics && blog.topics.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2">
                    {blog.topics.map((topic) => (
                      <Badge
                        key={topic.id || topic.name}
                        size="sm"
                        colorScheme="primary"
                        variant="solid"
                      >
                        {topic.name}
                      </Badge>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  {authorLabel && <span>{authorLabel}</span>}
                  {blog.publishedAt && authorLabel && <Icon icon={faDot} className="size-1" />}
                  {blog.publishedAt && <ContentDate value={blog.publishedAt} />}
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </ContentContainer>
  );
}
